import React from 'react';
import { Box, Chip, Tooltip, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  WorkspacePremium as PremiumIcon
} from '@mui/icons-material';

/**
 * Featured Badge komponens - Egységesített stílus
 * @param {object} featured - { tier: 'TOP' | 'TIER2' | 'TIER3', endsAt: Date }
 * @param {string} size - 'small' | 'medium' | 'large'
 * @param {string} variant - 'chip' | 'banner' | 'icon'
 */
const FeaturedBadge = ({ featured, size = 'small', variant = 'chip' }) => {
  const theme = useTheme();

  if (!featured) return null;

  const tierConfig = {
    TOP: {
      label: 'TOP',
      icon: TrophyIcon,
      gradient: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
      color: theme.palette.text.primary,
      shadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.25)}`, // MÓDOSÍTVA: Kisebb shadow
      borderColor: theme.palette.secondary.main,
      glowColor: alpha(theme.palette.secondary.main, 0.2)
    },
    TIER2: {
      label: 'KIEMELT',
      icon: StarIcon,
      gradient: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      color: theme.palette.primary.contrastText,
      shadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
      borderColor: theme.palette.primary.main,
      glowColor: alpha(theme.palette.primary.main, 0.2)
    },
    TIER3: {
      label: 'AJÁNLOTT',
      icon: PremiumIcon,
      gradient: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
      color: theme.palette.primary.contrastText,
      shadow: `0 2px 8px ${alpha(theme.palette.primary.light, 0.25)}`,
      borderColor: theme.palette.primary.light,
      glowColor: alpha(theme.palette.primary.light, 0.2)
    }
  };

  const config = tierConfig[featured.tier];
  const Icon = config.icon;

  // MÓDOSÍTVA: Egységesített méretek a többi chip-pel
  const sizeStyles = {
    small: { 
      fontSize: '0.7rem', // Mint a többi chip
      height: 24,         // Mint a többi chip
      iconSize: 14,       // Kisebb ikon
      px: 0.75            // Padding csökkentve
    },
    medium: { 
      fontSize: '0.75rem', 
      height: 28, 
      iconSize: 16, 
      px: 1 
    },
    large: { 
      fontSize: '0.8rem', 
      height: 32, 
      iconSize: 18, 
      px: 1.5 
    }
  };

  const style = sizeStyles[size];

  // Chip variáns (alapértelmezett - kártyákon használt)
  if (variant === 'chip') {
    return (
      <Tooltip 
        title={`Kiemelt szolgáltató - ${config.label} szint`}
        arrow
      >
        <Chip
          icon={<Icon sx={{ fontSize: style.iconSize, color: config.color }} />}
          label={config.label}
          size="small"
          sx={{
            background: config.gradient,
            color: config.color,
            fontWeight: 600, // MÓDOSÍTVA: 700 helyett 600 (mint a többi chip)
            fontSize: style.fontSize,
            height: style.height,
            px: style.px,
            boxShadow: config.shadow,
            border: `1px solid ${config.borderColor}`,
            fontFamily: theme.typography.fontFamily, // MÓDOSÍTVA: Theme font használata
            
            // MÓDOSÍTVA: Finomabb, lassabb animáció
            animation: 'subtlePulse 4s ease-in-out infinite',
            '@keyframes subtlePulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                boxShadow: config.shadow
              },
              '50%': {
                transform: 'scale(1.02)', // MÓDOSÍTVA: 1.05 helyett 1.02
                boxShadow: `0 3px 12px ${alpha(config.borderColor, 0.35)}` // MÓDOSÍTVA: Kisebb glow
              }
            },
            
            '& .MuiChip-icon': {
              marginLeft: 0.5,
              marginRight: -0.25 // MÓDOSÍTVA: Ikon és text közötti távolság csökkentése
            },
            
            '& .MuiChip-label': {
              paddingLeft: 0.5,
              paddingRight: 0.75
            }
          }}
        />
      </Tooltip>
    );
  }

  // Overlay variáns: fotó fölé kerülő jelvény (szolgáltatói kártya), animáció nélkül
  if (variant === 'overlay') {
    return (
      <Tooltip title={`Kiemelt szolgáltató - ${config.label} szint`} arrow enterTouchDelay={0}>
        <Box
          aria-label={`Kiemelt szolgáltató - ${config.label}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            height: 28,
            pl: 0.75,
            pr: 1.25,
            borderRadius: '14px',
            background: config.gradient,
            color: config.color,
            border: '1px solid rgba(255,255,255,0.7)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: 0.3,
            lineHeight: 1,
            fontFamily: theme.typography.fontFamily,
            whiteSpace: 'nowrap',
          }}
        >
          <Icon sx={{ fontSize: 16, color: config.color }} />
          {config.label}
        </Box>
      </Tooltip>
    );
  }

  // Banner variáns (profil oldalakon használható)
  if (variant === 'banner') {
    return (
      <Box
        sx={{
          background: config.gradient,
          color: config.color,
          p: 1.5,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: config.shadow,
          border: `1px solid ${config.borderColor}`,
          fontWeight: 600,
          fontSize: style.fontSize,
          fontFamily: theme.typography.fontFamily
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
        <Box sx={{ flex: 1 }}>
          {config.label} Kiemelt Szolgáltató
        </Box>
      </Box>
    );
  }

  // Icon variáns (kompakt megjelenítés)
  if (variant === 'icon') {
    return (
      <Tooltip 
        title={`Kiemelt szolgáltató - ${config.label} szint`}
        arrow
      >
        <Box
          sx={{
            background: config.gradient,
            borderRadius: '50%',
            width: style.height,
            height: style.height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: config.shadow,
            border: `1px solid ${config.borderColor}`
          }}
        >
          <Icon sx={{ fontSize: style.iconSize, color: config.color }} />
        </Box>
      </Tooltip>
    );
  }

  return null;
};

export default FeaturedBadge;